import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Select from '@/common/components/Select'
import { UnitTextField } from '@/common/components/UnitTextField'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { ProductData } from '@/common/types/product.type'
import { ContractProductCategoryProductItem } from '@/features/privateCompanies/components/updateContract/UpdateCreditLimitsModal/ContractProductCategoryProductItem'
import {
  UpdateContractCreditLimitsSchema,
  UpdateContractProductCategoryValidationSchema,
} from '@/features/privateCompanies/schemas/contract/updateCreditLimits.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { FileBoxIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, UseFieldArrayRemove, useFormContext } from 'react-hook-form'

interface ContractProductCategoryItemProps {
  contractProductCategory: UpdateContractProductCategoryValidationSchema
  productCategoryIndex: number
  removeContractProductCategory: UseFieldArrayRemove
  disabled: boolean
  contract?: CompanyContractType
}

export const ContractProductCategoryItem = ({
  contractProductCategory,
  productCategoryIndex,
  removeContractProductCategory,
  disabled,
  contract,
}: ContractProductCategoryItemProps) => {
  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<UpdateContractCreditLimitsSchema>()

  const {
    fields: contractProductCategoryProducts,
    append: appendContractProductCategoryProduct,
    remove: removeContractProductCategoryProduct,
  } = useFieldArray({
    control,
    name: `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products`,
  })

  const { data: productsOptions } = useProducts({
    filters: {
      category: contractProductCategory?.product_category?.id,
      search: searchProduct,
    },
    mapper: Mapper.mapToOptionsWithItem<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const handleAppendContractProductCategoryProduct = (product: ProductData) => {
    appendContractProductCategoryProduct({
      product,
      global_value_in_currency: '',
      global_value_in_unit_for_consumption: '',
      price_rule: null as any
    })
  }

  const watchRequireProductCreditQuota = watch(`contract.require_product_credit_quota`)

  return (
    <Box className="flex flex-col bg-white-200 gap-y-4">
      <div className="flex items-center">
        <div className="flex items-center gap-x-2 bg-white-200 rounded-l-sm px-2 py-1">
          <FileBoxIcon strokeWidth={1} className="size-6" />

          <p className="text-xs font-bold">{contractProductCategory?.product_category?.display_name}</p>
        </div>

        <Button
          name={`remove_product_category_${productCategoryIndex}`}
          onClick={() => removeContractProductCategory(productCategoryIndex)}
          className="size-8 bg-black hover:bg-black-900 rounded-none p-0"
          disabled={disabled}
        >
          <XIcon strokeWidth={1} />
        </Button>
      </div>

      <Select
        name={`search_product_${productCategoryIndex}`}
        placeholder="Pesquisar produtos"
        options={
          productsOptions?.filter((product) => {
            return !contractProductCategoryProducts?.some(({ product: { id } }) => id === product.value)
          }) || []
        }
        onChange={(option) => handleAppendContractProductCategoryProduct((option as { item: ProductData }).item)}
        onInputChange={setSearchProduct}
        value=""
        disabled={disabled}
      />

      {watchRequireProductCreditQuota && (
        <div className="flex gap-x-2">
          {contract?.consumption_controlled_by !== 'quantity' && (
            <Controller
              control={control}
              name={`contract.contract_product_categories_attributes.${productCategoryIndex}.global_value_in_currency`}
              render={({ field }) => (
                <CurrencyTextField
                  dataTestid={`contract_product_category_${productCategoryIndex}_global_value_in_currency_input`}
                  label="Cota total em valor"
                  markAsRequired
                  className="w-full"
                  {...field}
                  errorMessage={
                    errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
                      ?.global_value_in_currency?.message
                  }
                  disabled={false}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name={`contract.contract_product_categories_attributes.${productCategoryIndex}.global_value_in_unit_for_consumption`}
            render={({ field }) => (
              <UnitTextField
                dataTestid={`contract_product_category_${productCategoryIndex}_global_value_in_unit_for_consumption_input`}
                label="Cota total em quantidade"
                markAsRequired
                className="w-full"
                decimals={2}
                {...field}
                errorMessage={
                  errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
                    ?.global_value_in_unit_for_consumption?.message
                }
                disabled={false}
              />
            )}
          />
        </div>
      )}

      {!!contractProductCategoryProducts?.length && (
        <div className="flex flex-col gap-4">
          {
            contractProductCategoryProducts?.map((contractProductCategoryProduct, index) => (
              <ContractProductCategoryProductItem
                key={contractProductCategoryProduct.id}
                contractProduct={contractProductCategoryProduct}
                productCategoryIndex={productCategoryIndex}
                productIndex={index}
                removeContractProductCategoryProduct={removeContractProductCategoryProduct}
                disabled={disabled}
                contract={contract}
              />
            )
          )}
        </div>
      )}
    </Box>
  )
}
